/**
 * Analysis Chart Factory
 *
 * Transforms raw deal data into yearly aggregated metrics for visualization.
 *
 * Key transformations:
 * - Groups deals by year
 * - Calculates per-category count and volume metrics
 * - Applies sanitized key naming for safe object property access
 * - Rounds volumes to one decimal place for clean display
 *
 * Business Logic Notes:
 * - H1/H2 detection now handled via API's currentHalf field (not Quarter data)
 * - "Other" category always sorts last for visual consistency
 * - Grand totals computed from allData parameter for accurate cross-category sums
 */
import React, { useMemo, useState, useEffect } from "react";
import ResponsiveD3Container from "../shared/rendering/ResponsiveD3Container";
import ExpandedChartLayout from "../shared/layout/ExpandedChartLayout";
import { DualChartLayout } from "../shared/layout/ChartLayouts";
import UnifiedD3Chart from "../shared/rendering/UnifiedD3Chart";
import BaseExpandableChart from "../shared/base/BaseExpandableChart";
import {
  calculateYearlyData,
  extractCategories,
  getChartConfig,
} from "../shared/utils/ChartDataUtils";
import { getChartDims } from "../../../lib/utils";
import {
  CHART_MARGIN,
  EXPANDED_CHART_MARGIN,
  ENHANCED_COLOR_PALETTE,
} from "../../../lib/constants";

export interface AnalysisFactoryConfig {
  chartType: string;
  title: string;
  legendTitle: string | null;
  categoryField: string;
  colorMap: Record<string, string>;
  normalizeCategory?: (v: any) => string | null;
  normalizeCategoryExpanded?: ((v: any) => string | null) | null;
  filterDeals?: (deals: any[]) => any[];
  volumeLabel?: string;
  countLabel?: string;
}

export interface CategoryChartProps {
  data: any[];
  categories: string[];
  previewHideFinalYear?: boolean;
  isVolume: boolean;
  mode: string;
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  isExpanded?: boolean;
  colorOf: (c: string) => string;
  showTotal?: boolean;
  selectedCategories?: string[];
  yTickCount?: number | null;
  yTickValues?: number[] | null;
  children?: React.ReactNode;
}

export interface AnalysisChartComponentProps {
  deals: any[];
  allDeals?: any[];
  selectedCategories?: string[];
  expandedCategories?: string[] | null;
  yTickCount?: number | null;
  yTickValues?: number[] | null;
  mobileShowOnly?: string | null;
  currentHalf?: "H1" | "H2";
}

/**
 * Factory function that creates domain-specific analysis chart components.
 * Wires together category extraction, aggregation, and color mapping.
 * Special handling for cantons: different normalization in expanded view.
 * @example
 * const IndustryChart = createAnalysisChart({
 *   chartType: 'industry',
 *   categoryField: 'Industry',
 *   colorMap: INDUSTRY_COLOR_MAP
 * });
 */
const createAnalysisChart = (config: AnalysisFactoryConfig) => {
  const {
    chartType,
    title,
    legendTitle,
    categoryField,
    colorMap,
    normalizeCategory = (v) => v,
    normalizeCategoryExpanded = null,
    filterDeals = (deals) => deals,
  } = config;

  const CategoryChart: React.FC<CategoryChartProps> = ({
    data,
    categories,
    previewHideFinalYear,
    isVolume,
    mode,
    width,
    height,
    margin,
    isExpanded = false,
    colorOf,
    showTotal = false,
    selectedCategories = [],
    yTickCount = null,
    yTickValues = null,
    children,
  }) => {
    const metricSuffix = isVolume ? "__volume" : "__count";
    const displayCategories = selectedCategories.length > 0 ? selectedCategories : categories;

    return (
      <UnifiedD3Chart
        data={data}
        previewHideFinalYear={previewHideFinalYear}
        categories={displayCategories}
        allCategories={categories}
        isVolume={isVolume}
        mode={mode}
        width={width}
        height={height}
        margin={margin}
        isExpanded={isExpanded}
        colorOf={colorOf}
        showTotal={showTotal}
        metricSuffix={metricSuffix}
        yTickCount={yTickCount}
        yTickValues={yTickValues}
      >
        {children}
      </UnifiedD3Chart>
    );
  };

  const AnalysisChart: React.FC<AnalysisChartComponentProps> = ({
    deals,
    allDeals,
    selectedCategories = [],
    expandedCategories = null,
    yTickCount = null,
    yTickValues = null,
    mobileShowOnly = null,
    currentHalf,
  }) => {
    const {
      chartData,
      previewChartData,
      categories,
      colorOf,
      expandedCategoriesCalculated,
      expandedChartData,
      previewHideFinalYear,
    } = useMemo(() => {
      if (!deals?.length)
        return {
          chartData: [],
          previewChartData: [],
          categories: [],
          colorOf: () => "#000",
          expandedCategoriesCalculated: [] as string[],
          expandedChartData: [],
        };

      const filteredDeals = filterDeals(deals);
      const allFilteredDeals = allDeals ? filterDeals(allDeals) : filteredDeals;

      const baseGetCategoryValue = (item: any) => normalizeCategory(item[categoryField]);
      const getCategoryValue = (item: any) => baseGetCategoryValue(item);

      // Sort categories alphabetically with "Other" always last for visual consistency
      const sortCategories = (cats: string[]) => {
        return cats.sort((a, b) => {
          if (a === "Other") return 1;
          if (b === "Other") return -1;
          return a.localeCompare(b);
        });
      };

      const extractedCategories = sortCategories(
        extractCategories(filteredDeals, getCategoryValue)
      );
      const allExtractedCategories = sortCategories(
        extractCategories(allFilteredDeals, getCategoryValue)
      );

      const chartConfig = getChartConfig(chartType);
      // Calculate yearly data for main and preview charts
      // Both use the same logic, but preview may hide the final year if data is incomplete
      const yearlyData = calculateYearlyData(filteredDeals, {
        ...chartConfig,
        categories: extractedCategories,
        getCategoryValue,
        includeTotal: true,
        allData: allFilteredDeals,
      });

      const previewYearlyData = calculateYearlyData(filteredDeals, {
        ...chartConfig,
        categories: extractedCategories,
        getCategoryValue,
        includeTotal: true,
        allData: allFilteredDeals,
      });

      // Handle expanded view categories (used for canton grouping)
      let expandedCats = expandedCategories || extractedCategories;
      let expandedData = yearlyData;

      // Special handling for canton charts: use a different normalization in expanded view
      // This allows grouping minor cantons as "Other" in preview, but showing all in expanded
      if (normalizeCategoryExpanded && expandedCategories) {
        const expandedGetCategoryValue = (item: any) =>
          normalizeCategoryExpanded(item[categoryField]);
        // For canton charts, use allFilteredDeals to ensure all cantons are considered
        const dealsForExpanded = chartType === "canton" ? allFilteredDeals : filteredDeals;
        expandedCats = sortCategories(
          extractCategories(dealsForExpanded, expandedGetCategoryValue)
        );
        expandedData = calculateYearlyData(dealsForExpanded, {
          ...chartConfig,
          categories: expandedCats,
          getCategoryValue: expandedGetCategoryValue,
          includeTotal: true,
          allData: allFilteredDeals,
        });
      }

      // Assign colors: use predefined colorMap, fallback to palette rotation
      // Assign colors: use colorMap if available, otherwise rotate through palette
      const colorFn = (category: string) =>
        colorMap[category] ||
        ENHANCED_COLOR_PALETTE[
          allExtractedCategories.indexOf(category) % ENHANCED_COLOR_PALETTE.length
        ];

      return {
        chartData: yearlyData,
        previewChartData: previewYearlyData,
        categories: extractedCategories,
        colorOf: colorFn,
        expandedCategoriesCalculated: expandedCats,
        expandedChartData: expandedData,
        // Use currentHalf from API to determine if preview should hide final year
        // H1 means only first half of year is available; hide incomplete year in preview
        previewHideFinalYear: currentHalf === "H1",
      };
    }, [deals, allDeals, expandedCategories, currentHalf]);

    const dims = getChartDims(false, undefined, CHART_MARGIN);
    const expandedDims = getChartDims(true, 440, EXPANDED_CHART_MARGIN);

    // Chart rendering helper: wraps CategoryChart in a responsive container
    const renderChart =
      (isVolume: boolean) =>
      ({ data, mode, isExpanded = false, width, height, showTotal = false, children }: any) => {
        const currentDims = isExpanded ? expandedDims : dims;
        const finalHeight = typeof height === "number" ? height : currentDims.height;

        return (
          <ResponsiveD3Container width="100%" height={finalHeight}>
            <CategoryChart
              previewHideFinalYear={previewHideFinalYear}
              data={data}
              categories={categories}
              isVolume={isVolume}
              mode={mode}
              margin={currentDims.margin}
              isExpanded={isExpanded}
              colorOf={colorOf}
              showTotal={showTotal}
              selectedCategories={selectedCategories}
              yTickCount={yTickCount}
              yTickValues={yTickValues}
            >
              {children}
            </CategoryChart>
          </ResponsiveD3Container>
        );
      };

    const VolumeChart = renderChart(true);
    const CountChart = renderChart(false);

    const ExpandedChart: React.FC<{
      data: any[];
      mode: string;
      expandedChart: string;
      showTotal?: boolean;
      controls?: React.ReactNode;
    }> = ({ data, mode, expandedChart, showTotal, controls }) => {
      const isVolumeChart = expandedChart === "volume";

      // Modal maintains its own selection state for interactive legend filtering
      // This allows users to toggle categories on/off in the expanded view independently
      // from the main chart filter state
      // See: https://react.dev/reference/react/useState
      const [modalSelectedCategories, setModalSelectedCategories] = useState<string[]>(
        expandedCategoriesCalculated || categories
      );

      // Reset modal selection when expanded categories change
      const expandedCategoriesKey = JSON.stringify(expandedCategoriesCalculated || []);
      const categoriesKey = JSON.stringify(categories || []);

      useEffect(() => {
        setModalSelectedCategories(expandedCategoriesCalculated || categories);
      }, [expandedCategoriesKey, categoriesKey]);

      /**
       * Toggle category selection in modal legend.
       * Allows multi-select for expanded chart legend.
       * @param cat - Category to toggle
       */
      const toggleModalCategory = (cat: string) => {
        setModalSelectedCategories((prev) => {
          if (!prev) return [cat];
          if (prev.includes(cat)) return prev.filter((c) => c !== cat);
          return [...prev, cat];
        });
      };

      const chartDataToUse =
        expandedChartData && expandedChartData.length > 0 ? expandedChartData : data;

      return (
        <ExpandedChartLayout
          legendItems={expandedCategoriesCalculated || categories}
          legendTitle={legendTitle}
          colorOf={colorOf}
          height={expandedDims.height}
          controls={controls}
          legendSelectable={chartType === "canton"}
          selectedLegendItems={modalSelectedCategories}
          onToggleLegend={toggleModalCategory}
        >
          <CategoryChart
            data={chartDataToUse}
            categories={expandedCategoriesCalculated || categories}
            isVolume={isVolumeChart}
            mode={mode}
            margin={expandedDims.margin}
            isExpanded={true}
            colorOf={colorOf}
            showTotal={showTotal}
            selectedCategories={modalSelectedCategories}
            yTickCount={yTickCount}
            yTickValues={yTickValues}
          />
        </ExpandedChartLayout>
      );
    };

    // Special case: timeline chart uses different component structure
    if (chartType === "timeline") {
      const TimelineChart = require("../timeline/TimelineChart").default;
      return (
        <TimelineChart
          data={chartData}
          previewData={previewChartData}
          previewHideFinalYear={previewHideFinalYear}
          showVolume={false}
          title={title}
          yTickCount={yTickCount}
          yTickValues={yTickValues}
          mobileShowOnly={mobileShowOnly}
        />
      );
    }

    return (
      <BaseExpandableChart
        chartTitle={title}
        data={chartData}
        previewData={previewChartData}
        ChartComponent={({ data, leftMode, rightMode, showTotal, onExpand, children }) => (
          <DualChartLayout
            volumeData={data}
            countData={data}
            VolumeChart={VolumeChart}
            CountChart={CountChart}
            volumeProps={{ mode: leftMode, showTotal }}
            countProps={{ mode: rightMode, showTotal }}
            volumeTitle={config.volumeLabel || "Invested capital"}
            countTitle={config.countLabel || "Number of deals"}
            onVolumeExpand={() => onExpand("volume")}
            onCountExpand={() => onExpand("count")}
            mobileShowOnly={mobileShowOnly}
            showHeaders={!mobileShowOnly}
            children={children}
          />
        )}
        ExpandedChartComponent={ExpandedChart}
        isDualChart={true}
        supportsSingleMode={true}
        supportsTotal={true}
        initialLeftMode="line"
        initialRightMode="line"
        initialShowTotal={false}
      />
    );
  };

  return AnalysisChart;
};

export default createAnalysisChart;
